/**
 * Creates hierarchical menu based on WordPress menu.
 * @link https://www.wpgraphql.com/docs/menus/#hierarchical-data
 */
import React, { useState, useEffect, useRef } from "react"
import { StaticImage } from "gatsby-plugin-image"
import UniversalLink from "§/src/utils/UniversalLink"
import classNames from "classnames"

/**
 * Internal dependencies.
 */
import "./_main-nav.scss"

// Assets
import LogoCittaDiVenezia from "§/src/images/logo/citta-di-venezia.svg"
import LogoVeneziaUnica from "§/src/images/logo/citypass-venezia-unica.svg"
import ArrowRight from "§/src/images/svg/arrow-right.svg"
import Hamburger from "§/src/images/svg/hamburger.svg"
import Close from "§/src/images/svg/x-close.svg"

// Components
import LangMenu from '§/src/components/menus/lang-menu';

// Language
import { useTranslation } from "react-i18next"

const MainNav = props => {
  const { t } = useTranslation()

  // Use state to track menu open/close status
  const [menuActive, setMenuActive] = useState(false);

  // References to avoid hydration issues
  const menuMobileRef = useRef(null);
  const hamburgerRef = useRef(null);
  const closeRef = useRef(null);

  const rawData = props.rawData
  const langMenu = rawData.langMenu
  const lang = props.lang
  const general = rawData.wp.generalSettings
  const pageUri = lang?.uri

  const language = lang?.locale?.locale

  // Language-specific content
  const translations = {
    it_IT: {
      contributoAccesso: "Scopri il contributo di accesso",
      urlContributo: "https://cda.ve.it/it/",
      vcr: "Venezia con Rivo",
      urlVcr: "/venezia-con-rivo/",
      risultati2024: "Risultati 2024",
      urlRisultati2024: "/campagna-2024/"
    },
    en_US: {
      contributoAccesso: "Discover the access fee",
      urlContributo: "https://cda.ve.it/en/",
      vcr: "Venezia with Rivo",
      urlVcr: "/en/venezia-with-rivo/",
      risultati2024: "2024 Results",
      urlRisultati2024: "/en/2024-results/"
    }
  }

  // Get translations based on current language
  const currentTranslation = translations[language] || translations.it_IT;

  // Function to handle link click for conversion tracking
  const handleLinkClick = (event) => {
    event.preventDefault();

    // Check if gtag function exists before calling
    if (typeof gtag_report_conversion_header_cda === 'function') {
      gtag_report_conversion_header_cda();
    }

    window.open(currentTranslation.urlContributo, "_blank");
  };

  // Function to toggle mobile menu
  const toggleMenu = () => {
    setMenuActive(!menuActive);
  }

  // Effect to ensure elements are properly referenced after initial render
  useEffect(() => {
    // Make sure we're in the browser environment
    if (typeof window !== 'undefined') {
      menuMobileRef.current = document.querySelector('.menu-mobile .menu-mobile-inner');
      hamburgerRef.current = document.querySelector('.menu-mobile .hamburger-default');
      closeRef.current = document.querySelector('.menu-mobile .hamburger-close');
    }
  }, []);

  // Effect to apply active class based on state
  useEffect(() => {
    if (menuMobileRef.current) {
      if (menuActive) {
        menuMobileRef.current.classList.add('active');
        hamburgerRef.current.classList.remove('active');
        closeRef.current.classList.add('active');
        // Add menu-open class to body
        document.body.classList.add('menu-open');
      } else {
        menuMobileRef.current.classList.remove('active');
        hamburgerRef.current.classList.add('active');
        closeRef.current.classList.remove('active');
        // Remove menu-open class from body
        document.body.classList.remove('menu-open');
      }
    }
  }, [menuActive]);

  // Clean up effect to ensure body class is removed when component unmounts
  useEffect(() => {
    return () => {
      document.body.classList.remove('menu-open');
    };
  }, []);

  return (
    <nav id="main-nav" className="navbar navbar-expand-lg bg-primary">
      <div className="container justify-content-end">
        <UniversalLink
          className="navbar-brand col-auto py-3 d-block me-auto"
          rel="home"
          to={language !== "it_IT" ? "/en/" : "/"}
          title={props.title}
          tagline={props.tagline}
        >
          <LogoCittaDiVenezia className="logocittadivenezia" />
          <LogoVeneziaUnica className="logoveneziaunica" />
        </UniversalLink>

        <div className="menu-desktop d-flex flex-row">
          <UniversalLink
            className="btn btn-sm fw-400 p-0"
            rel="home"
            to={currentTranslation.urlVcr}
          >
            <span className={currentTranslation.urlVcr === pageUri ? "active" : ""}>{currentTranslation.vcr}</span>
          </UniversalLink>

          <UniversalLink
            className="btn btn-sm fw-400 p-0"
            rel="home"
            to={currentTranslation.urlRisultati2024}
          >
            <span className={currentTranslation.urlRisultati2024 === pageUri ? "active" : ""}>{currentTranslation.risultati2024}</span>
          </UniversalLink>

          <LangMenu lang={lang} wpMenu={langMenu} general={general} className="dropdown m-0" id="lang-top" buttonClass="btn-link fw-400 px-0 px-md-2" theme="dropdown-menu-light" />

          <div className="mainctacontainer">
            <a
              href={currentTranslation.urlContributo}
              onClick={handleLinkClick}
              target="_blank"
              id="main-cta"
              className="btn btn-light"
              rel="noreferrer"
              title={currentTranslation.contributoAccesso}
              tagline={currentTranslation.contributoAccesso}
            >
              <span>{currentTranslation.contributoAccesso}</span>
              <ArrowRight />
            </a>
            <div className="ruler btn btn-light">
              <span>{currentTranslation.contributoAccesso}</span>
              <ArrowRight />
            </div>
          </div>
        </div>

        <section className="menu-mobile">
            <div className={`menu-mobile-inner position-fixed bg-dark-red ${menuActive ? 'active' : ''}`}>
                <div className="container">
                    <div className="menu-entries d-flex flex-column">
                        <UniversalLink
                          className="btn btn-sm fw-800 p-0"
                          rel="home"
                          to={currentTranslation.urlVcr}
                        >
                          <span className={currentTranslation.urlVcr === pageUri ? "active" : ""}>{currentTranslation.vcr}</span>
                        </UniversalLink>

                        <UniversalLink
                          className="btn btn-sm fw-800 p-0"
                          rel="home"
                          to={currentTranslation.urlRisultati2024}
                        >
                          <span className={currentTranslation.urlRisultati2024 === pageUri ? "active" : ""}>{currentTranslation.risultati2024}</span>
                        </UniversalLink>

                        <a
                          href={currentTranslation.urlContributo}
                          target="_blank"
                          onClick={handleLinkClick}
                          id="main-cta-mobile"
                          className="btn btn-sm fw-800 p-0"
                          rel="noreferrer"
                          title={currentTranslation.contributoAccesso}
                          tagline={currentTranslation.contributoAccesso}
                        >
                          <span>{currentTranslation.contributoAccesso}</span>
                        </a>

                        <LangMenu
                          lang={lang}
                          wpMenu={langMenu}
                          general={general}
                          className="dropdown m-0"
                          id="lang-top"
                          buttonClass="btn-link fw-400 px-0 px-md-2"
                          theme="dropdown-menu-light"
                        />
                    </div>
                </div>

                <div className="rivo position-absolute">
                  <StaticImage
                    src="../../../images/rivo/rivo-video.webp"
                    placeholder="blurred"
                    alt="rivo-video"
                  />
                </div>
            </div>

            <div className="hamburger" onClick={toggleMenu}>
                <div className={`hamburger-default ${!menuActive ? 'active' : ''}`}>
                    <Hamburger />
                </div>
                <div className={`hamburger-close position-absolute ${menuActive ? 'active' : ''}`}>
                    <Close />
                </div>
            </div>
        </section>
      </div>
    </nav>
  )
}

export default MainNav
